import relationships from 'Consts/Relationships';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import classNames from 'classnames';

const featureEnabled = (resource, relationship) => (!!(resource && resource.relationship(relationship)));
export const featuresEnabled = (resource, relationshipArr) => relationshipArr.every(relationship => featureEnabled(resource, relationship));
/**
 * @deprecated use withRelationship instead
 */
export const hasRelationship = (resource, relationship) => featureEnabled(resource, relationship); // Used when you need to check a single relationship for non feature toggle reasons
export const hasRelationships = (resource, relationshipArr) => featuresEnabled(resource, relationshipArr); // Used when you need to check relationships for non feature toggle reasons

/**
 * Pick an implementation based off of feature toggles.
 * @function
 * @deprecated 0.0.0 Use pick() instead
 *
 * @see webapp/src/Components/Templates/Picker.jsx
 *
 * @param {Object}                            resource        A Facet Resource
 * @param {Object.<string, CallableFunction>} implementations An object with resource name keys and function as the value
 * @param {Object}                            [args]          Options
 *
 * @returns {CallableFunction}
 */
export const pickImplementation = (resource, implementations, args = {}) => {
  const {
    immediatelyInvokeFunctions = false,
  } = args;
  let selectedImplementation;

  if (resource) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [feature, implementation] of Object.entries(implementations)) {
      if (feature !== relationships.default) {
        if (featureEnabled(resource, feature)) {
          selectedImplementation = implementation;
        }
      }
    }
  }

  if (!selectedImplementation && implementations[relationships.default] !== undefined) {
    selectedImplementation = implementations[relationships.default];
  }

  if (selectedImplementation === undefined) {
    throw new Error('no implementation provided');
  }

  switch (typeof selectedImplementation) {
    case 'function':
      if (immediatelyInvokeFunctions) {
        return selectedImplementation();
      }

      return selectedImplementation;
    default:
      return selectedImplementation;
  }
};

/**
 * Pick a function based off of feature toggles.
 * @function
 * @deprecated 0.0.0 Use pick() instead
 */
export const pickFunction = (resource, functions) => {
  return pickImplementation(resource, functions, {
    immediatelyInvokeFunctions: true,
  });
};

/**
 * @arg {fr.Resource} resource
 * @arg {Array<Array<string, *>>} featureValuePairList
 * @example resource X has the 'test' & 'test2' enabled
 * // returns A
 * pickWithPriority(X, [['test', 'A'], ['test2', 'B']], null)
 * @example resource Z doesn't have a toggle enabled
 * // returns null
 * pickWithPriority(Z, [['test', 'A'], ['test2', 'B']], null)
 * @returns {*}
 * @deprecated 0.0.0 Use pick() instead
 */
export const pickWithPriority = (resource, featureValuePairList, defaultValue = () => null) => {
  for (const [feature, value] of featureValuePairList) {
    if (featureEnabled(resource, feature)) {
      return value;
    }
  }

  return defaultValue;
};

/**
 * @template T
 * @arg {fr.Resource} resource
 * @arg {Record<string, T>} featureValues
 * @arg {T} defaultValue
 * @example resource X has the 'test' & 'test2' enabled
 * // returns A
 * pick(X, {test: 'A', test2:, 'B'}, null)
 * @example resource Z doesn't have a toggle enabled
 * // returns null
 * pick(Z, {test: 'A', test2:, 'B'}, null)
 * @returns {T}
 */
export const pick = (resource, featureValues, defaultValue) => {
  const entries = Object.entries(featureValues);
  for (const [feature, value] of entries) {
    if (featureEnabled(resource, feature)) {
      return value;
    }
  }

  return defaultValue;
};

/**
 * @deprecated - use pick
 */
export const pickComponentAndProps = (resource, implementations, args = {}) => {
  const {
    showLoadingIndicator = true,
  } = args;

  if (!resource) {
    return {
      component: (showLoadingIndicator) ? LoadingIndicator : null,
      props: null,
    };
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const [feature, implementation] of Object.entries(implementations)) {
    if (implementation !== null && (!Array.isArray(implementation) || implementation.length !== 2)) {
      throw new Error(`Expected null or array with 2 arguments in implementation: component and props. Got ${implementation}`);
    }

    if (feature !== relationships.default) {
      if (featureEnabled(resource, feature)) {
        if (Array.isArray(implementation)) {
          return { component: implementations[feature][0], props: implementations[feature][1] };
        }
        return { component: null, props: null };
      }
    }
  }
  if (implementations[relationships.default] !== undefined) {
    if (Array.isArray(implementations[relationships.default])) {
      return { component: implementations[relationships.default][0], props: implementations[relationships.default][1] };
    }
    return { component: null, props: null };
  }
  throw new Error('no default [component, props] set provided');
};


/**
 * Helper function that allows for picking style classes based on feature toggles.
 * @param resource {object} - The resource to check for the feature toggle.
 * @param styles {Object<string, Array<string | Object<string, boolean>>>} - Styles to be passed into `classNames` function.
 * @see pickComponentAndProps
 * @see pickImplementation
 * @returns string
 * @example
 * const styles = pickStyles(userResource, {
 *     [relationships.default]: [
 *       classes.red, {
 *         [classes.open]: false,
 *         [classes.closed]: true
 *       }],
 *     [relationships.feature]: [
 *       classes.red,
 *       classes.blue, {
 *         [classes.open]: false,
 *         [classes.closed]: true
 *       }],
 * });
 *
 * <div className={styles} />
 */

export const pickStyles = (resource, styles) => {
  if (!resource) {
    return '';
  }

  if (!styles[relationships.default]) {
    throw new Error('Expected default style');
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const [feature, styleSet] of Object.entries(styles)) {
    if (styleSet === null || styleSet === undefined) {
      throw new Error(`Expected styles for feature ${feature}`);
    }

    if (!Array.isArray(styleSet)) {
      throw new Error(`Expected styles for ${feature} to be of type Array`);
    }

    if (featureEnabled(resource, feature)) {
      return classNames(...styleSet);
    }
  }

  return classNames(...styles[relationships.default]);
};

export default featureEnabled;

export function followRelationship(relationship, body = undefined, facetRestOpts = undefined) {
  if (!relationship) {
    return undefined;
  }

  const action = relationship.meta('actions') ? relationship.meta('actions')[0] : 'GET';
  switch (action.toUpperCase()) {
    case 'GET':
      return relationship.link('related').get(facetRestOpts);
    case 'POST':
      return relationship.link('related').post(body || {}, facetRestOpts);
    case 'PUT':
      return relationship.link('related').put(body || {}, facetRestOpts);
    case 'DELETE':
      return relationship.link('related').delete(facetRestOpts);
    default:
      throw new Error(`Unknown action ${action}`);
  }
}
